import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ActorPicker from "../../components/AppointmentPlanner/ActorPicker";
import PlanningComparison from "../../components/AppointmentPlanner/PlanningComparison";
import styles from "../../components/AppointmentPlanner/AppointmentPlanner.module.css";
import SaturnePage from "../../components/SaturnePage/SaturnePage";
import { getErrorMessage } from "../../services/ErrorMessage";
import { ActorModel, SupportedActorType } from "../../services/fhir/fhir.types";
import {
  ActorPickerKind,
  AppointmentDraft,
  PlanningComparison as PlanningComparisonModel,
  PlanningSuggestion,
} from "../../services/scheduling/AppointmentPlanning.types";
import { createSchedulingServices } from "../../services/scheduling/SchedulingServiceFactory";

function toIso(value: string): string {
  return dayjs(value).toISOString();
}

function initialStart(): string {
  return dayjs().add(1, "day").startOf("day").add(8, "hour").format("YYYY-MM-DDTHH:mm");
}

function initialEnd(): string {
  return dayjs().add(2, "day").startOf("day").add(18, "hour").format("YYYY-MM-DDTHH:mm");
}

export default function NewAppointmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const services = useMemo(createSchedulingServices, []);
  const [actors, setActors] = useState<ActorModel[]>([]);
  const [pickerKind, setPickerKind] = useState<ActorPickerKind>();
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [rangeStart, setRangeStart] = useState(initialStart);
  const [rangeEnd, setRangeEnd] = useState(initialEnd);
  const [comparison, setComparison] = useState<PlanningComparisonModel>();
  const [selected, setSelected] = useState<PlanningSuggestion>();
  const [selectedSlotIdByActor, setSelectedSlotIdByActor] = useState<Record<string, string | undefined>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [created, setCreated] = useState(false);

  const applySelection = (suggestion: PlanningSuggestion) => {
    setSelected(suggestion);
    setSelectedSlotIdByActor(
      Object.fromEntries(
        actors.map((actor) => [
          actor.reference,
          suggestion.compatibleSlotIdsByActor[actor.reference]?.[0],
        ]),
      ),
    );
  };

  const addActor = async (actorType: SupportedActorType, id: string) => {
    try {
      const actor = await services.actorService.getById(actorType, id);
      if (!actor) {
        throw new Error(t("planner.errors.actorNotFound"));
      }
      setActors((current) =>
        current.some((candidate) => candidate.reference === actor.reference)
          ? current
          : [...current, actor],
      );
      setPickerKind(undefined);
      setComparison(undefined);
      setSelected(undefined);
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    }
  };

  const removeActor = (reference: string) => {
    setActors((current) => current.filter((actor) => actor.reference !== reference));
    setComparison(undefined);
    setSelected(undefined);
  };

  const compare = async () => {
    setLoading(true);
    setError(undefined);
    setCreated(false);
    try {
      const result = await services.appointmentPlanningService.compare(
        actors,
        { start: toIso(rangeStart), end: toIso(rangeEnd) },
        durationMinutes,
      );
      setComparison(result);
      setSelected(undefined);
      setSelectedSlotIdByActor({});
    } catch (compareError) {
      setComparison(undefined);
      setError(getErrorMessage(compareError));
    } finally {
      setLoading(false);
    }
  };

  const selectStart = (start: string) => {
    if (!comparison) {
      return;
    }
    try {
      applySelection(services.appointmentPlanningService.select(comparison, start));
    } catch (selectionError) {
      setError(getErrorMessage(selectionError));
    }
  };

  const createAppointment = async () => {
    if (!selected) {
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const draft: AppointmentDraft = {
        title,
        comment,
        actors,
        durationMinutes,
        range: { start: toIso(rangeStart), end: toIso(rangeEnd) },
        selected,
        selectedSlotIdByActor,
      };
      await services.appointmentPlanningService.create(draft);
      setCreated(true);
    } catch (saveError) {
      setError(getErrorMessage(saveError));
      setCreated(false);
    } finally {
      setSaving(false);
    }
  };

  const selectedActorsByType = (types: SupportedActorType[]) =>
    actors.filter((actor) => types.includes(actor.type));

  return (
    <SaturnePage loading={false} needsLogin wide>
      <div className={`${styles.page} appointment-planner-page`}>
        <header className={styles.hero}>
          <div>
            <span>{t("planner.eyebrow")}</span>
            <h1>{t("planner.title")}</h1>
            <p>{t("planner.subtitle")}</p>
          </div>
          <button type="button" className={styles.cancelButton} onClick={() => navigate("/MySchedules")}>
            {t("planner.cancel")}
          </button>
        </header>

        {error && <div className={styles.error} role="alert">{error}</div>}
        {created && (
          <div className={styles.success} role="status">
            <strong>{t("planner.created")}</strong>
            <button type="button" onClick={() => navigate("/MySchedules")}>{t("planner.backToCalendars")}</button>
          </div>
        )}

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <div>
              <span>{t("planner.people.eyebrow")}</span>
              <h2>{t("planner.people.title")}</h2>
            </div>
            <div className={styles.addButtons}>
              <button type="button" onClick={() => setPickerKind("participant")}>{t("planner.people.addParticipant")}</button>
              <button type="button" onClick={() => setPickerKind("location")}>{t("planner.people.addLocation")}</button>
              <button type="button" onClick={() => setPickerKind("device")}>{t("planner.people.addDevice")}</button>
            </div>
          </div>

          <div className={styles.actorGroups}>
            {[
              { key: "participants", label: t("planner.people.participants"), actors: selectedActorsByType(["patient", "practitioner"]) },
              { key: "locations", label: t("planner.people.locations"), actors: selectedActorsByType(["location"]) },
              { key: "devices", label: t("planner.people.devices"), actors: selectedActorsByType(["device"]) },
            ].map((group) => (
              <div key={group.key} className={styles.actorGroup}>
                <strong>{group.label}</strong>
                <div className={styles.chips}>
                  {group.actors.length === 0 && <span className={styles.emptyChip}>{t("planner.people.none")}</span>}
                  {group.actors.map((actor) => (
                    <span key={actor.reference} className={styles.actorChip}>
                      <i>{t(`calendar.actorTypes.${actor.type}`)}</i>
                      {actor.display}
                      <button type="button" aria-label={t("planner.people.remove", { name: actor.display })} onClick={() => removeActor(actor.reference)}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {pickerKind && (
          <ActorPicker
            kind={pickerKind}
            serverUrl={process.env.REACT_APP_FHIR_URL ?? "fhir"}
            onAdd={(type, id) => void addActor(type, id)}
            onClose={() => setPickerKind(undefined)}
            onError={() => setError(t("planner.errors.search"))}
          />
        )}

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <div>
              <span>{t("planner.period.eyebrow")}</span>
              <h2>{t("planner.period.title")}</h2>
            </div>
          </div>
          <div className={styles.formGrid}>
            <label>
              {t("planner.fields.title")}
              <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t("planner.fields.titlePlaceholder")} />
            </label>
            <label>
              {t("planner.fields.duration")}
              <select value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.target.value))}>
                {[15, 30, 45, 60, 90, 120].map((minutes) => <option key={minutes} value={minutes}>{minutes} min</option>)}
              </select>
            </label>
            <label>
              {t("planner.fields.from")}
              <input type="datetime-local" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
            </label>
            <label>
              {t("planner.fields.to")}
              <input type="datetime-local" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
            </label>
            <label className={styles.commentField}>
              {t("planner.fields.comment")}
              <textarea value={comment} onChange={(event) => setComment(event.target.value)} />
            </label>
          </div>
          <div className={styles.compareActions}>
            <button type="button" className={styles.primaryButton} disabled={actors.length === 0 || loading} onClick={() => void compare()}>
              {loading ? t("planner.comparing") : t("planner.compare")}
            </button>
          </div>
        </section>

        <PlanningComparison
          comparison={comparison}
          selected={selected}
          onSelect={applySelection}
          onSelectStart={selectStart}
        />

        {selected && (
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <div>
                <span>{t("planner.allocation.eyebrow")}</span>
                <h2>{t("planner.allocation.title")}</h2>
              </div>
              <strong>{dayjs(selected.start).format("ddd D MMM · HH:mm")} – {dayjs(selected.end).format("HH:mm")}</strong>
            </div>
            <div className={styles.allocations}>
              {actors.map((actor) => {
                const options = selected.compatibleSlotIdsByActor[actor.reference] ?? [];
                const conflicts = selected.conflictsByActor[actor.reference] ?? [];
                return (
                  <label key={actor.reference}>
                    <span>
                      <strong>{actor.display}</strong>
                      <small>{t(`calendar.actorTypes.${actor.type}`)}</small>
                      {conflicts.length > 0 && (
                        <small className={styles.conflictText}>
                          {t("planner.allocation.conflicts", { count: conflicts.length })}
                        </small>
                      )}
                    </span>
                    {options.length > 0 ? (
                      <select
                        value={selectedSlotIdByActor[actor.reference] ?? ""}
                        onChange={(event) => setSelectedSlotIdByActor((current) => ({ ...current, [actor.reference]: event.target.value || undefined }))}
                      >
                        <option value="">{t("planner.allocation.noSlot")}</option>
                        {options.map((id) => <option key={id} value={id}>{t("planner.allocation.slot", { id })}</option>)}
                      </select>
                    ) : (
                      <span className={styles.warning}>{t("planner.allocation.unlinked")}</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className={styles.createActions}>
              <button type="button" className={styles.primaryButton} disabled={saving || created} onClick={() => void createAppointment()}>
                {saving ? t("planner.creating") : t("planner.create")}
              </button>
            </div>
          </section>
        )}
      </div>
    </SaturnePage>
  );
}
